import { useRouter } from "next/router";
import { useUserContext } from "../../contexts/UserProvider";
import { axiosApiInstance } from "../../pages/api/axios";
import { sleep } from "../../utils/sleep";
import { useMoneyHashQuery, type QueryResponse } from "../useMoneyHashQuery";

const featureFlagList = [
  "routing_request_extra_actions",
  "new_dashboard_summaries",
  "intent_details_in_webhook",
  "enable_smart_recurring_actions",
  "hold_and_wait",
  "network_tokenization",
  "settlement_and_reconciliation",
  "bin_lookup",
  "new_dashboard_sr_and_ar",
  "settlement_analytics",
  "default_routing_action_execution",
  "datadog_dashboard_analytics",
  "show_billing_dashboard",
  "bin_lookup_active_organization",
  "account_based_permissions",
  "new_intent_details_design",
  "dashboard_transaction_anomalies",
  "loyalty_earn_dashboard",
  "require_intent_secret_for_client_api",
  "new_dashboard_rates_from_marts",
] as const;

type Feature = (typeof featureFlagList)[number];

type FeatureItemResponse = {
  enabled: boolean;
  name: Feature;
}[];

export type FeatureFlags = Record<Feature, boolean>;

export function useFeatureFlags() {
  const { id: userId, organizationId } = useUserContext();
  const router = useRouter();

  const { data, isLoading } = useMoneyHashQuery<Record<Feature, boolean>>({
    queryKey: [
      "featureFlags",
      { names: featureFlagList.join(","), userId, organizationId },
    ],
    queryFn: async ({ signal }) => {
      const featureFlags = axiosApiInstance
        .get<QueryResponse<FeatureItemResponse>>("/features/", {
          params: {
            names: featureFlagList.join(","),
            user_id: userId,
            organization_id: organizationId,
          },
          signal,
        })
        .then((res) => {
          return {
            ...res.data,
            data: res.data.data.reduce(
              (acc, feature) => {
                acc[feature.name] = feature.enabled;
                return acc;
              },
              {} as Record<Feature, boolean>
            ),
          };
        });

      await sleep(500);
      return featureFlags;
    },
    staleTime: Infinity,
    enabled:
      (!!userId && !!organizationId) ||
      (router.isReady && !!router.query.access_token), // is in platformConnect mode
  });

  return {
    featureFlags: data || ({} as Record<Feature, boolean>),
    isLoadingFeatureFlags: isLoading,
  };
}
